import { IconRefresh } from "@tabler/icons-react";
import {
	Background,
	Controls,
	type Edge,
	Handle,
	MarkerType,
	type Node,
	Position,
	ReactFlow,
	useNodesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
	Button,
	Card,
	Checkbox,
	Flex,
	Segmented,
	Space,
	Tag,
	Tooltip,
	Typography,
	theme,
} from "antd";
import { useMemo, useState } from "react";
import {
	useProjectCatalogGraph,
	useRefreshProjectCatalogGraph,
} from "@/common/artemis-operations/project-repositories";
import {
	type CatalogEntity,
	CatalogFileStatus,
	type CatalogGraph,
	CatalogRelationType,
	type CatalogRepositoryStatus,
} from "@/common/model";
import {
	BaseContainer,
	CustomEmpty,
	CustomIcon,
	FullSpin,
	QueryGuard,
} from "../base";
import { CustomList } from "../custom-list";
import {
	crossesRepositories,
	EXTERNAL_REPOSITORY_ID,
	layeredLayout,
	rollUpToRepositories,
} from "./catalog-graph-layout";

const { Text } = Typography;

type GraphView = "entities" | "repositories";

const KIND_COLORS: Record<string, string> = {
	Component: "blue",
	API: "purple",
	Resource: "orange",
	System: "green",
	Domain: "cyan",
};

const RELATION_LABELS: Record<string, string> = {
	[CatalogRelationType.DEPENDS_ON]: "depends on",
	[CatalogRelationType.PROVIDES_API]: "provides",
	[CatalogRelationType.CONSUMES_API]: "consumes",
	[CatalogRelationType.PART_OF]: "part of",
};

const STATUS_TAGS: Record<string, { color: string; label: string }> = {
	[CatalogFileStatus.FOUND]: { color: "success", label: "Found" },
	[CatalogFileStatus.MISSING]: { color: "default", label: "No catalog file" },
	[CatalogFileStatus.INVALID]: { color: "error", label: "Invalid" },
	[CatalogFileStatus.UNAVAILABLE]: { color: "warning", label: "Unavailable" },
};

/** Kinds that only group other entities; hidden with the "part of" links. */
const GROUPING_KINDS = new Set(["System", "Domain"]);

interface EntityNodeData extends Record<string, unknown> {
	entity: CatalogEntity;
}

interface RepositoryNodeData extends Record<string, unknown> {
	label: string;
	entityCount: number;
	external: boolean;
}

const handleStyle = { width: 8, height: 8, border: "2px solid white" };

function NodeHandles({ color }: { color: string }) {
	return (
		<>
			<Handle
				type="target"
				position={Position.Left}
				style={{ ...handleStyle, left: -4, background: color }}
			/>
			<Handle
				type="source"
				position={Position.Right}
				style={{ ...handleStyle, right: -4, background: color }}
			/>
		</>
	);
}

function EntityNode({ data: { entity } }: { data: EntityNodeData }) {
	const { token } = theme.useToken();
	const details = [entity.type, entity.lifecycle].filter(Boolean).join(" · ");
	const tooltip = (
		<Flex vertical gap={4}>
			<Text strong style={{ color: "inherit" }}>
				{entity.ref}
			</Text>
			{entity.description && <span>{entity.description}</span>}
			{entity.owner && <span>Owner: {entity.owner}</span>}
			{entity.tags.length > 0 && <span>Tags: {entity.tags.join(", ")}</span>}
		</Flex>
	);

	return (
		<>
			<NodeHandles color={token.colorPrimary} />
			<Tooltip title={tooltip} placement="top">
				<Card
					size="small"
					styles={{ body: { padding: 12 } }}
					style={{
						width: 240,
						borderRadius: 10,
						borderStyle: entity.isExternal ? "dashed" : "solid",
						opacity: entity.isExternal ? 0.75 : 1,
						boxShadow: token.boxShadowTertiary,
					}}
				>
					<Flex vertical gap={4}>
						<Flex justify="space-between" align="center" gap={8}>
							<Text strong ellipsis>
								{entity.title ?? entity.name}
							</Text>
							<Tag
								color={KIND_COLORS[entity.kind]}
								style={{ marginInlineEnd: 0 }}
							>
								{entity.kind}
							</Tag>
						</Flex>
						{details && <Text style={{ fontSize: 12 }}>{details}</Text>}
						<Text ellipsis style={{ fontSize: 12 }}>
							{entity.isExternal
								? "Not in this project"
								: entity.repositoryFullName}
						</Text>
					</Flex>
				</Card>
			</Tooltip>
		</>
	);
}

function RepositoryNode({ data }: { data: RepositoryNodeData }) {
	const { token } = theme.useToken();
	return (
		<>
			<NodeHandles color={token.colorPrimary} />
			<Card
				size="small"
				styles={{ body: { padding: 12 } }}
				style={{
					width: 240,
					borderRadius: 10,
					borderStyle: data.external ? "dashed" : "solid",
					boxShadow: token.boxShadowTertiary,
				}}
			>
				<Flex vertical gap={4}>
					<Text strong ellipsis>
						{data.label}
					</Text>
					<Text style={{ fontSize: 12 }}>
						{data.entityCount} {data.entityCount === 1 ? "entity" : "entities"}
					</Text>
				</Flex>
			</Card>
		</>
	);
}

const nodeTypes = { entity: EntityNode, repository: RepositoryNode };

/** Theme colors for edges, read from the antd token so both light and dark themes work. */
interface EdgeColors {
	primary: string;
	muted: string;
	text: string;
	background: string;
}

function edgeStyle(
	highlight: boolean,
	colors: EdgeColors,
	// Bezier curves separate per endpoint pair; smoothstep reads better in dense graphs but
	// routes edges from one node through a shared channel, hiding all but one of them.
	type: "smoothstep" | "default" = "smoothstep",
) {
	const stroke = highlight ? colors.primary : colors.muted;
	return {
		type,
		animated: highlight,
		// Marker size is in stroke widths, so keep it small or thick edges get huge heads.
		markerEnd: {
			type: MarkerType.ArrowClosed,
			width: 10,
			height: 10,
			color: stroke,
		},
		style: { stroke, strokeWidth: highlight ? 2 : 1.5 },
		labelStyle: { fontSize: 11, fontWeight: 500, fill: colors.text },
		labelBgStyle: { fill: colors.background, fillOpacity: 0.95 },
		labelBgPadding: [4, 2] as [number, number],
		labelBgBorderRadius: 4,
	};
}

/** Builds the entity-level nodes and edges. Cross-repository relations are highlighted. */
function entityElements(
	graph: CatalogGraph,
	showPartOf: boolean,
	colors: EdgeColors,
): { nodes: Node[]; edges: Edge[] } {
	const entities = graph.entities.filter(
		(e) => showPartOf || !GROUPING_KINDS.has(e.kind),
	);
	const visible = new Set(entities.map((e) => e.ref));
	const relations = graph.relations.filter(
		(r) =>
			(showPartOf || r.type !== CatalogRelationType.PART_OF) &&
			visible.has(r.sourceRef) &&
			visible.has(r.targetRef),
	);
	const byRef = new Map(entities.map((e) => [e.ref, e]));
	const repositoryOf = new Map(
		entities.map((e) => [e.ref, e.projectRepositoryId]),
	);
	const positions = layeredLayout(
		entities.map((e) => e.ref),
		relations.map((r) => ({ source: r.sourceRef, target: r.targetRef })),
		(ref) => byRef.get(ref)?.name ?? ref,
		(ref) => byRef.get(ref)?.repositoryFullName ?? "~",
	);

	return {
		nodes: entities.map((entity) => ({
			id: entity.ref,
			type: "entity",
			position: positions[entity.ref],
			data: { entity } satisfies EntityNodeData,
		})),
		edges: relations.map((r) => ({
			id: `${r.sourceRef}|${r.type}|${r.targetRef}`,
			source: r.sourceRef,
			target: r.targetRef,
			label: RELATION_LABELS[r.type] ?? r.type,
			...edgeStyle(crossesRepositories(r, repositoryOf), colors),
		})),
	};
}

/**
 * Builds the repository-level rollup: one node per repository, one edge per pair of repositories
 * a dependency or API relation connects.
 */
function repositoryElements(
	graph: CatalogGraph,
	colors: EdgeColors,
): { nodes: Node[]; edges: Edge[] } {
	// Belonging to a system declared in another repository is not a dependency on that repository.
	const { nodes, edges } = rollUpToRepositories(
		graph.entities,
		graph.relations.filter((r) => r.type !== CatalogRelationType.PART_OF),
	);
	const positions = layeredLayout(
		nodes.map((n) => n.id),
		edges,
		(id) => nodes.find((n) => n.id === id)?.label ?? id,
		undefined,
		{ x: 400, y: 140 },
	);

	return {
		nodes: nodes.map((n) => ({
			id: n.id,
			type: "repository",
			position: positions[n.id],
			data: {
				label: n.label,
				entityCount: n.entityCount,
				external: n.id === EXTERNAL_REPOSITORY_ID,
			} satisfies RepositoryNodeData,
		})),
		edges: edges.map((e) => ({
			id: `${e.source}|${e.target}`,
			source: e.source,
			target: e.target,
			label: `${e.types.map((t) => RELATION_LABELS[t] ?? t).join(", ")}${e.count > 1 ? ` (${e.count})` : ""}`,
			...edgeStyle(e.target !== EXTERNAL_REPOSITORY_ID, colors, "default"),
		})),
	};
}

function RepositoryStatusList({
	repositories,
}: {
	repositories: CatalogRepositoryStatus[];
}) {
	return (
		<CustomList
			data={repositories}
			emptyText="linked repositories"
			renderItem={(r) => (
				<Flex vertical gap={2}>
					<Text strong>{r.repositoryFullName}</Text>
					<Text type="secondary">
						{r.status === CatalogFileStatus.FOUND
							? `${r.path} · ${r.entityCount} ${r.entityCount === 1 ? "entity" : "entities"}`
							: null}
						{r.status === CatalogFileStatus.FOUND && r.message ? " · " : null}
						{r.message}
					</Text>
				</Flex>
			)}
			renderTrailing={(r) => {
				const tag = STATUS_TAGS[r.status];
				return <Tag color={tag?.color}>{tag?.label ?? r.status}</Tag>;
			}}
		/>
	);
}

/**
 * The project's dependency chart, built from the Backstage `catalog-info.yaml` in each linked
 * repository. The entity view draws every Component, API and Resource; the repository view rolls
 * that up to show which repositories depend on each other.
 */
export function ProjectCatalogGraph({ projectId }: { projectId: string }) {
	const { token } = theme.useToken();
	const { data, isPending, isError, dataUpdatedAt } =
		useProjectCatalogGraph(projectId);
	const refresh = useRefreshProjectCatalogGraph(projectId);
	const [view, setView] = useState<GraphView>("entities");
	const [showPartOf, setShowPartOf] = useState(false);

	const colors = useMemo(
		(): EdgeColors => ({
			primary: token.colorPrimary,
			muted: token.colorTextQuaternary,
			text: token.colorText,
			background: token.colorBgContainer,
		}),
		[
			token.colorPrimary,
			token.colorTextQuaternary,
			token.colorText,
			token.colorBgContainer,
		],
	);
	const elements = useMemo(() => {
		if (!data) return { nodes: [], edges: [] };
		return view === "entities"
			? entityElements(data, showPartOf, colors)
			: repositoryElements(data, colors);
	}, [data, view, showPartOf, colors]);

	const definedCount = (data?.entities ?? []).filter(
		(e) => !e.isExternal,
	).length;
	const repositories = data?.repositories ?? [];
	const missingCount = repositories.filter(
		(r) => r.status === CatalogFileStatus.MISSING,
	).length;

	return (
		<QueryGuard
			isPending={isPending}
			isError={isError}
			isNull={!data}
			pending={
				// Reading catalog-info.yaml from every linked repository takes a while.
				<div style={{ padding: 48 }}>
					<FullSpin description="Reading catalog files…" />
				</div>
			}
		>
			<Space vertical style={{ width: "100%" }} size="large">
				{definedCount === 0 ? (
					<CustomEmpty description="Dependencies" expanded>
						<Text>
							Add a Backstage <Text code>catalog-info.yaml</Text> to the root of
							a linked repository to chart its components, APIs and resources.
						</Text>
					</CustomEmpty>
				) : (
					<Flex vertical gap={12}>
						<Flex justify="space-between" align="center" wrap gap={8}>
							<Segmented<GraphView>
								value={view}
								onChange={setView}
								options={[
									{ label: "Entities", value: "entities" },
									{ label: "Repositories", value: "repositories" },
								]}
							/>
							<Flex align="center" gap={12}>
								{view === "entities" && (
									<Checkbox
										checked={showPartOf}
										onChange={(e) => setShowPartOf(e.target.checked)}
									>
										Systems and domains
									</Checkbox>
								)}
								<Tooltip title="Re-read the catalog files">
									<Button
										type="text"
										loading={refresh.isPending}
										icon={<CustomIcon icon={IconRefresh} />}
										onClick={() => refresh.mutate()}
									/>
								</Tooltip>
							</Flex>
						</Flex>
						<div
							style={{
								height: 560,
								border: `1px solid ${token.colorBorderSecondary}`,
								borderRadius: token.borderRadiusLG,
							}}
						>
							{/* Remount per graph so the viewport refits — see DeploymentGraphFlow. */}
							<CatalogFlow
								key={`${view}:${showPartOf}:${dataUpdatedAt}`}
								nodes={elements.nodes}
								edges={elements.edges}
							/>
						</div>
						<Text type="secondary">
							Highlighted links cross between repositories. Dashed nodes are
							referenced but not defined by any linked repository.
						</Text>
					</Flex>
				)}

				{missingCount > 0 && (
					<Text type="secondary">
						{missingCount} of {repositories.length} linked{" "}
						{repositories.length === 1 ? "repository has" : "repositories have"}{" "}
						no <Text code>catalog-info.yaml</Text>.
					</Text>
				)}

				<BaseContainer size="small" title="Catalog files">
					<RepositoryStatusList repositories={repositories} />
				</BaseContainer>
			</Space>
		</QueryGuard>
	);
}

/** Renders one graph with draggable nodes. Mounted fresh per graph — useNodesState seeds once. */
function CatalogFlow({ nodes, edges }: { nodes: Node[]; edges: Edge[] }) {
	const [nodesState, , onNodesChange] = useNodesState(nodes);
	return (
		<ReactFlow
			nodes={nodesState}
			edges={edges}
			onNodesChange={onNodesChange}
			nodeTypes={nodeTypes}
			fitView
			nodesConnectable={false}
			elementsSelectable={false}
			nodesDraggable
			minZoom={0.2}
			maxZoom={1.5}
		>
			<Background />
			<Controls showInteractive={false} />
		</ReactFlow>
	);
}
